import * as React from 'react';
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useField, useFormikContext } from 'formik';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useSelector } from 'react-redux';
import { Component } from '../../../models/be/db/FormSpecification';
import { stopPropagationAndSetState, hasValue, getCustomerById } from '../../../lib/utils';
import { getCandidatePrimaryName } from '../../../core/api';
import ErrorMessage from '../../../commonComponents/ErrorMessage';
import { useAsyncAction } from '../../../commonComponents/Hooks';
import MessageBox from '../MessageBox';
import PrimaryName from '../../../models/be/PrimaryName';
import { CheckboxInput } from '../fields/Checkbox';
import RootState from '../../../models/RootState';
import { customerContext } from '../../../contexts/customer/CustomerContext';
import { getCustomerIdFromAccessToken } from '../../../core/session';
import { getClientCustomerDetails } from '../../../core/customerApi';
import { getCustomerAddress } from '../../../core/coreApi';
import ConsentDocumentAIS from '../../pages/ConsentDocumentAIS';

const personalDetails = 'personalDetails';
const nationalPolicyHistoryCheck = 'nationalPolicyHistoryCheck';
const nzNationalPolicyHistoryCheck = 'nzNationalPolicyHistoryCheck';
const afpPoliceCheck = 'afpPoliceCheck';
const complyAdvantageConsent = 'complyAdvantageConsent';
const dbsBasicPrivacyPolicy = 'dbsBasicPrivacyPolicy';
const dbsStandardEnhancedPrivacyPolicy = 'dbsStandardEnhancedPrivacyPolicy';
const dbsCheckResultConsent = 'dbsCheckResultConsent';
const dbsBasicCheckResultConsent = 'dbsBasicCheckResultConsent';
const dbsDeclaration = 'dbsDeclaration';
const dbsUnspentConvictions = 'dbsUnspentConvictions';
const dbsUpdateServiceConsent = 'dbsUpdateServiceConsent';
const dsPrivacy = 'dsPrivacy';
const dsConsent = 'dsConsent';
const vevoConsent = 'vevoConsent';
const noMiddleNames = 'noMiddleNames';
const iDontUseASurname = 'iDontUseASurname';
const nzMojStatement = 'nzMojStatement';
const genericConsent = 'genericConsent';
const internationalCheckConsent = 'internationalCheckConsent';
const referenceCheckConsent = 'referenceCheckConsent';
const qualificationDeclaration = 'qualificationDeclaration';
const workingWithChildrenCheck = 'workingWithChildrenCheck';

const Statement = (props: StatementProps) => {
	if (props.fieldNames.length < 2) {
		throw new Error('ConsentDate field is missing.');
	}
	const context = React.useContext(customerContext);
	const { candidateId, fieldNames, workflowPrimaryName, workflowId } = props;
	const [fieldName, consentDateFieldName] = fieldNames;
	const { statementType } = props.props;
	const [field, meta] = useField({ name: fieldName, type: 'checkbox' });
	const value = field.value ?? '';
	const { setFieldValue } = useFormikContext();
	const customerDetails = useSelector((state: RootState) => state?.customerDetails);
	const customerIdFromAccessToken = getCustomerIdFromAccessToken();
	const customerName = () =>
		hasValue(customerDetails)
			? customerDetails.customerName
			: getCustomerById(context.customer.value, customerIdFromAccessToken)?.customerName;
	const [isTermsAndConditionsVisible, setIsTermsAndConditionsVisible] = useState(false);
	const [fullPrimaryName, setFullPrimaryName] = useState<string>(null);
	const [customerAddress, setCustomerAddress] = useState<string>(null);
	const [customerPublicDisplayName, setCustomerPublicDisplayName] = useState<string>(null);
	const [, errorMessage, performAsyncAction] = useAsyncAction();
	const abortSingle = new AbortController().signal;

	async function fetchCandidateAndCustomerUserName() {
		await performAsyncAction(async () => {
			let name =
				workflowPrimaryName?.firstName && workflowPrimaryName?.lastName
					? `${workflowPrimaryName.firstName} ${workflowPrimaryName.lastName}`
					: null;
			if (!hasValue(name)) {
				const primaryName = await getCandidatePrimaryName(candidateId);
				name = `${primaryName.firstName} ${primaryName.lastName}`;
			}
			setFullPrimaryName(name);
		});
	}

	async function fetchCustomerAddress() {
		await performAsyncAction(async () => {
			const addr = await getCustomerAddress(customerIdFromAccessToken, abortSingle);
			setCustomerAddress(addr);
		});
	}

	async function fetchCustomerPublicDisplayName() {
		await performAsyncAction(async () => {
			if (customerDetails?.publicDisplayName) {
				setCustomerPublicDisplayName(customerDetails.publicDisplayName);
			} else {
				const subCustomer = await getClientCustomerDetails(customerIdFromAccessToken, abortSingle);
				setCustomerPublicDisplayName(subCustomer.publicDisplayName);
			}
		});
	}

	useEffect(() => {
		fetchCandidateAndCustomerUserName();
		if (statementType === nationalPolicyHistoryCheck) {
			fetchCustomerAddress();
			fetchCustomerPublicDisplayName();
		}
	}, []);

	const getCustomerUserFullName = () => {
		return props.customerUserName ?? 'Client';
	};

	const setConsentDateField = () => {
		// Set the `shouldValidate` to false for date field because:
		// 1. formLevelValidation only validates consentGiven field;
		// 2. If validate both fields at the same time, Yup will get incorrect values for validation of each field.
		setFieldValue(consentDateFieldName, new Date().toISOString(), false);
	};

	field.onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setConsentDateField();
		setFieldValue(fieldName, e.target.checked, true);
	};

	const closeDialog = () => {
		setIsTermsAndConditionsVisible(false);
	};

	return (
		<>
			{errorMessage && <MessageBox error={errorMessage} />}
			<CheckboxWrapper>
				<CheckboxInput name={fieldName} value={value} onChange={field.onChange} />
				<label htmlFor={fieldName} className="label k-checkbox-label">
					{getStatementText(statementType, getCustomerUserFullName(), setIsTermsAndConditionsVisible, customerName())}
				</label>
			</CheckboxWrapper>
			{isTermsAndConditionsVisible && (
				<div onClick={stopPropagationAndSetState(closeDialog)}>
					<Dialog title="Terms and Conditions" onClose={closeDialog} className="inForm">
						{statementType === nationalPolicyHistoryCheck && (
							<CrimTermsAndConditionsView
								fullPrimaryName={fullPrimaryName}
								CustomerPublicDisplayName={customerPublicDisplayName}
								CustomerAddress={customerAddress}
							/>
						)}
						{statementType === nzNationalPolicyHistoryCheck && (
							<NzpvTermsAndConditionsView fullPrimaryName={fullPrimaryName} />
						)}
						{statementType === vevoConsent && <VevoTermsAndConditionsView />}
						{statementType === afpPoliceCheck && <AfpTermsAndConditionsView />}
						{statementType === internationalCheckConsent && (
							<>
								<InternationCheckTermsView customerName={customerName()} />
								{workflowId && <ConsentDocumentAIS workflowId={workflowId} />}
							</>
						)}
					</Dialog>
				</div>
			)}
			{meta.touched && <ErrorMessage message={meta.error} />}
		</>
	);
};

const getStatementText = (
	statementType: string,
	customerUserFullName: string,
	setIsTermsAndConditionsVisible: (isVisible: boolean) => void,
	customerPublicName?: string
) => {
	if (statementType === personalDetails) {
		return (
			<span className="consentContent">
				I am authorised to provide the personal details presented and I consent to my information being checked with the
				document issuer or official record holder via third party systems for the purpose of confirming my identity.
			</span>
		);
	}
	if (statementType === nationalPolicyHistoryCheck) {
		return (
			<span className="consentContent">
				I have read and understood{' '}
				<a href="#" onClick={() => setIsTermsAndConditionsVisible(true)}>
					the terms
				</a>{' '}
				and I consent to VETTING.com undertaking a nationally coordinated criminal history check on my behalf.
			</span>
		);
	}
	if (statementType === workingWithChildrenCheck) {
		return (
			<span className="consentContent">
				I consent to my personal details being used in accordance with the screening requirements of the organisation
				who requested this check to verify my current, valid Working with Children check details. To register a new
				Working with Children check or renew an existing one, please do so through the appropriate state body first
				before using this form. Links to the state bodies are available{' '}
				<a
					href="https://www.acic.gov.au/services/national-police-checking-service/find-out-more-information/working-children-checks"
					target="_blank"
					rel="noreferrer noopener"
				>
					here on the official ACIC website
				</a>{' '}
				after clicking on the <em>Where can I get a working with children check?</em> section.
			</span>
		);
	}
	if (statementType === nzNationalPolicyHistoryCheck) {
		return (
			<span className="consentContent">
				I have read{' '}
				<a href="#" onClick={() => setIsTermsAndConditionsVisible(true)}>
					the terms
				</a>{' '}
				and I consent to VETTING.com undertaking a New Zealand Police Vetting Check on my behalf.
			</span>
		);
	}
	if (statementType === afpPoliceCheck) {
		return (
			<span className="consentContent">
				I have read{' '}
				<a href="#" onClick={() => setIsTermsAndConditionsVisible(true)}>
					the terms
				</a>{' '}
				and I consent to VETTING.com undertaking an AFP Police Check on my behalf.
			</span>
		);
	}
	if (statementType === complyAdvantageConsent) {
		return (
			<span className="consentContent">
				I consent to my personal details being used in accordance with the screening requirements of the organisation
				who requested this check.
			</span>
		);
	}
	if (statementType === dbsBasicPrivacyPolicy) {
		return (
			<span className="consentContent">
				I have read the Privacy notice for DBS Disclosure Services{' '}
				<a
					href="https://www.gov.uk/government/publications/dbs-disclosure-services-privacy-notice"
					target="_blank"
					rel="noreferrer noopener"
				>
					DBS Disclosure Services Privacy Notice - GOV.UK
				</a>{' '}
				and I understand how DBS will process my personal data.
			</span>
		);
	}
	if (statementType === dbsStandardEnhancedPrivacyPolicy) {
		return (
			<span className="consentContent">
				I have read the Privacy notice for DBS Disclosure Services{' '}
				<a
					href="https://www.gov.uk/government/publications/dbs-disclosure-services-privacy-notice"
					target="_blank"
					rel="noreferrer noopener"
				>
					DBS Disclosure Services Privacy Notice - GOV.UK
				</a>{' '}
				and I understand how DBS will process my personal data and the options available to me for submitting an
				application.
			</span>
		);
	}
	if (statementType === dbsCheckResultConsent) {
		return (
			<span className="consentContent">
				<p>
					I consent to the DBS providing an electronic result directly to the registered body that has submitted my
					application. I understand that an electronic result contains a message that indicates either the certificate
					is blank or to await certificate which will indicate that my certificate contains information. In some cases
					the registered body may provide this information directly to my employer prior to me receiving my certificate.
				</p>

				<p>
					I understand if I do not consent to an electronic result being issued to the registered body submitting my
					application that I must not proceed with this application and I should submit a paper application form.
				</p>

				<p>
					I understand that to withdraw my consent whilst my application is in progress I must contact the DBS helpline
					03000 200 190. My application will then be withdrawn.
				</p>
			</span>
		);
	}
	if (statementType === dbsBasicCheckResultConsent) {
		return (
			<span className="consentContent">
				<p>
					I consent to the DBS providing an electronic result directly to the responsible organisation that has
					submitted my application. I understand that an electronic result contains a message that indicates either the
					certificate does not contain criminal record information or to await certificate which will indicate that my
					certificate contains criminal record information. In some cases the responsible organisation may provide this
					information directly to my employer prior to me receiving my certificate.
				</p>

				<p>
					I understand if I do not consent to an electronic result being issued to the responsible organisation
					submitting my application that I must not proceed with this application and I should apply directly to DBS
					Request a basic DBS check - <a href="https://www.gov.uk/">GOV.UK (Welcome to GOV.UK)</a>.
				</p>

				<p>
					I understand that to withdraw my consent whilst my application is in progress I must contact the DBS helpline
					03000 200 190. My application will then be withdrawn.
				</p>
			</span>
		);
	}
	if (statementType === dbsDeclaration) {
		return (
			<span className="consentContent">
				I have provided (and will provide) complete and true information in support of the application and I understand
				that knowingly making a false statement for this purpose is a criminal offence.
			</span>
		);
	}
	if (statementType === dbsUnspentConvictions) {
		return (
			<span className="consentContent">
				Do you have any convictions or cautions (excluding youth cautions) which would not be filtered in line with
				current guidance? Please click here to view the
				<a href="https://www.gov.uk/government/publications/dbs-filtering-guidance/dbs-filtering-guide">
					DBS Filtering Guidance
				</a>
			</span>
		);
	}
	if (statementType === dbsUpdateServiceConsent) {
		return (
			<span className="consentContent">
				I give authority for {customerPublicName} to receive up-to-date information in relation to my criminal record
				DBS Certificate
			</span>
		);
	}
	if (statementType === dsPrivacy) {
		return (
			<span className="consentContent">
				<p>
					Disclosure Scotland (&#34;we&#34;) are an agency of the Scottish Government and the services we provide are
					governed by the Police Act 1997 and the Protection of Vulnerable Groups (Scotland) Act 2007.
				</p>
				<p>
					Questions, queries and comments in relation to how we process your personal information are welcome and should
					be addressed to our Data Protection Officer at <a href="mailto:DSDPO@gov.scot">DSDPO@gov.scot</a>.
				</p>
				<p>
					Disclosure Scotland is fully committed to compliance with all applicable data protection legislation and our
					operations and processes are in accordance with law. Where you provide us with consent to do so, we will
					collect personal information about you which you provide to us in your application, from police forces and
					from police records. Our services are used to prevent crime and protect the vulnerable. The information we
					collect will only be disclosed to legitimate organisations who have a legal right to access this information.
					Disclosure Scotland reserves the right to share information with the police where it believes a crime may have
					been committed.
				</p>
				<p>
					We will retain your data for a period of ten years. We protect your data in line with UK government
					information security standards and regularly test the security of our systems. All staff processing Disclosure
					Scotland applications are background checked before they are provided access to our systems.
				</p>
				<p>We only process your information where you have provided us with your consent to do so.</p>
				<p>For more details about how we process your personal information, please see our privacy policy at</p>
				<p>
					<a
						href="https://www.mygov.scot/privacy/?via=http://www.disclosurescotland.co.uk/about/PrivacyPolicyandDisclaimer.htm"
						target="_blank"
						rel="noreferrer noopener"
					>
						https://www.mygov.scot/privacy/?via=http://www.disclosurescotland.co.uk/about/PrivacyPolicyandDisclaimer.htm
					</a>
				</p>
			</span>
		);
	}
	if (statementType === dsConsent) {
		return (
			<ConsentContainer>
				<li>
					I understand the following:
					<ul>
						<li>
							Disclosure Scotland will use the information I have given to verify my identity and to check and process
							my application. Disclosure Scotland will use this information for the purposes of prevention or detection
							of crime and for other related purposes.
						</li>
						<li>
							Disclosure Scotland may pass the information it holds about me to other Government departments or
							organisations, the police and other law enforcement agencies for the purpose of the prevention and
							detection of crime, of the apprehension and prosecution of offenders and for other related purposes.
						</li>
					</ul>
				</li>
				<li>
					I declare that the information I have given is complete and correct. I understand that to knowingly make a
					false statement in this application is a criminal offence. I will give any additional information that may be
					required to verify the information given and will immediately notify any changes to this information.
				</li>
				<li>I give my consent for the Responsible Body to receive a copy of my certificate to their office address.</li>
			</ConsentContainer>
		);
	}
	if (statementType === vevoConsent) {
		return (
			<span className="consentContent">
				I have read the{' '}
				<a href="#" onClick={() => setIsTermsAndConditionsVisible(true)}>
					Consent Statement
				</a>{' '}
				and agree to proceed with this check.
			</span>
		);
	}
	if (statementType === noMiddleNames) {
		return (
			<span className="consentContent">
				<p>I confirm I have no middle names</p>
			</span>
		);
	}
	if (statementType === iDontUseASurname) {
		return (
			<span className="consentContent">
				<p>I don&apos;t use a surname and I have the identification documents to prove it (e.g. Cher)</p>
			</span>
		);
	}
	if (statementType === nzMojStatement) {
		return (
			<span className="consentContent">
				I declare that the information I give in this request is, to the best of my knowledge, true, complete and
				correct. I understand that if I have provided incorrect or incomplete information, the Ministry of Justice may
				not be able to provide an accurate result. I authorise the disclosure of criminal conviction information to
				VETTING.com and the organisation who requested this check.
			</span>
		);
	}
	if (statementType === genericConsent) {
		return (
			<span className="consentContent">
				I consent to my personal details being used in accordance with the screening requirements of the organisation
				who requested this check.
			</span>
		);
	}
	if (statementType === referenceCheckConsent) {
		return (
			<span className="consentContent">
				I provide my consent for{' '}
				<a href="https://vetting.com/" target="_blank" rel="noreferrer">
					VETTING.com
				</a>{' '}
				to obtain a personal reference, a reference for my previous employment, or a reference for my education
				experience, as applicable
			</span>
		);
	}
	if (statementType === internationalCheckConsent) {
		return <InternationalCheckDeclaration setIsTermsAndConditionsVisible={setIsTermsAndConditionsVisible} />;
	}
	if (statementType === qualificationDeclaration) {
		return <QualificationDeclaration />;
	}
	throw new Error(`Unknown statementType: ${statementType}.`);
};

interface StatementProps extends Component {
	candidateId: string;
	workflowPrimaryName: PrimaryName;
	customerUserName: string;
	workflowId?: string;
}

Statement.defaultProps = {
	workflowId: null
};

export default Statement;

const CrimTermsAndConditionsView = (props: {
	fullPrimaryName: string;
	CustomerPublicDisplayName: string;
	CustomerAddress: string;
}) => {
	return (
		<section className="tcWrapper">
			<div className="achcTcs">
				<p>I, {props.fullPrimaryName && <>{props.fullPrimaryName}</>}</p>
				<ol>
					<li>
						acknowledge that I/the Applicant am aware that I/the Applicant am providing consent for a Nationally
						Coordinated Criminal History Check to be conducted using all Personal Information provided in this form and
						in supplied identity documents.
					</li>
					<li>
						acknowledge that the accredited body named VETTING.com of PO Box 1164, Mona Vale, NSW 1660 on behalf of
						it&#39;s Customer ({props.CustomerPublicDisplayName} of {props.CustomerAddress}) is collecting information
						in this form to provide to the Australian Criminal Intelligence Commission (ACIC) and police agencies, for a
						Nationally Coordinated Criminal History Check to be conducted for the purpose outlined in the Occupation
						section of this form.
					</li>
					<li>
						give consent to Police Information relating to me/the Applicant, being disclosed in accordance with the
						purpose identified in Occupation section of this form, and applicable legislation and information release
						policies (including spent convictions legislation described in Australian Government and state or territory
						legislation).
					</li>
					<li>
						acknowledge that it is usual practice for my/the Applicant&#39;s Personal Information (including Police
						Information) to be used by police agencies and the ACIC for law enforcement, including purposes set out in
						the <i>Australian Crime Commission Act 2002</i> (Cth).
					</li>
					<li>
						will fully and accurately complete this form, and the Personal Information I/the Applicant provide relates
						to me/the Applicant.
					</li>
					<li>
						acknowledge that withholding and/or providing misleading or false information on this form and in any
						supplied identity documents is a Commonwealth offence and may lead to prosecution under the{' '}
						<i>Criminal Code Act 1995 </i>(Cth).
					</li>
					<li>
						acknowledge that any information sent by mail or electronically, in relation to this form, including
						identity documents, is sent at my/the Applicant&#39;s risk and I/the Applicant, am aware of the consequences
						of sending information in these ways.
					</li>
					<li>
						give consent to VETTING.com verifying the identity documents that I/the Applicant supplied using an
						Authoritative Source. such as VEVO or DVS.
					</li>
					<li>
						give consent to VETTING.com providing the document identifiers of the documents that I/the Applicant
						supplied to the ACIC for the purpose of the ACIC using the DVS to verify my/the Applicant&#39;s identity
						documents.
					</li>
					<li>
						give consent to the ACIC using the document identifiers of the documents that I/the Applicant supplied to
						verify my/the Applicant&#39;s identity documents using the DVS.
					</li>
					<li>
						give consent to the ACIC and police agencies using and disclosing my/the Applicant&#39;s Personal
						Information that I/ the Applicant, have provided in this form, Personal Information contained in my supplied
						identity documents and all other available Personal Information relating to me/the Applicant, to conduct a
						Nationally Coordinated Criminal History Check.
					</li>
					<li>
						give consent to the ACIC disclosing the Police Information sourced from the police agencies to other
						approved bodies and VETTING.com.
					</li>
					<li>
						give consent to the Accredited Body disclosing my/the Applicant&#39;s Personal Information (including Police
						Information) to the Accredited Body&#39;s contractors for the purpose of conducting a Nationally Coordinated
						Criminal History Check.
					</li>
					<li>
						give consent to VETTING.com disclosing to the Customer, my/the Applicant&#39;s Personal Information
						(including Police Information) to assess my/the Applicant&#39;s suitability for the purpose identified in
						the Occupation section of this form and as required by Australian law.
					</li>
					<li>acknowledge that I/the Applicant have read and understand the information on this form.</li>
				</ol>
			</div>
		</section>
	);
};

const NzpvTermsAndConditionsView = (props: { fullPrimaryName: string }) => {
	return (
		<section className="tcWrapper">
			<div className="achcTcs">
				<p>I, {props.fullPrimaryName && <>{props.fullPrimaryName}</>}</p>
				<ol>
					<li>
						The New Zealand Police may release any information they hold if relevant to the purpose of this vetting
						request. This includes:
						<ol type="a">
							<li>Conviction histories and infringement/demerit reports. </li>
							<li>Active investigations, charges and warrants to arrest.</li>
							<li>
								Charges that did not result in a conviction including those that were acquitted (not guilty), discharged
								without conviction or withdrawn.
							</li>
							<li>
								Any interaction I have had with New Zealand Police relevant to the role being vetted, including
								investigations that did not result in prosecution or were resolved by an alternative resolution
								programme.
							</li>
							<li>
								Information regarding family violence where I was the victim, offender or witness to an incident or
								offence. This is particularly relevant where the role being vetted for takes place in a home environment
								where exposure to physical or verbal violence could place vulnerable persons at emotional or physical
								risk.
							</li>
							<li>
								Information subject to name suppression where that information is necessary for the purpose of the vet.
							</li>
						</ol>
					</li>
					<li>
						If I am eligible under the Criminal Records (Clean Slate) Act 2004, my conviction history will not be
						released unless:
						<ol type="a">
							<li>
								Section 19(3) of the Clean Slate Act applies to this request (exceptions to the clean slate regime).
							</li>
							<li>
								Section 31(3) of the Children&#39;s Act 2014 applies to this request (safety checks of core
								children&#39;s workers).
							</li>
							<li>
								The vetting request is made for the purpose of an overseas visa/work permit and authorises the vetting
								report to be provided directly to the relevant embassy, high commission, or consulate. Please see the
								vetting website for more information regarding the Clean Slate legislation and what may be released.
							</li>
						</ol>
					</li>
					<li>
						The Police Vetting Service may disclose newly obtained relevant information to the requesting agency after
						the completion of the Police vet in the following circumstances:
						<ol type="a">
							<li>
								The disclosure of the newly obtained information is justified under the Privacy Act 2020 (if it had
								existed or been available at the time of the Police vet, it would have been disclosed); and
							</li>
							<li>
								The Police Vetting Service has taken steps to confirm that the purpose for the Police vet still exists –
								e.g., that I am employed or engaged in a role that required a Police vet. The Police Vetting Service
								will take reasonable steps to notify you prior to the disclosure.
							</li>
						</ol>
					</li>
					<li> Information provided in this consent form may be used to update New Zealand Police records.</li>
					<li>
						I am entitled to a copy of the vetting report released to the agency (to be provided by the agency) and can
						request a correction of any personal information by contacting the Police Vetting Service.
					</li>
					<li>
						Please notify the agency or the Police Vetting Service if you wish to withdraw your consent. For further
						information about the vetting process, please see the vetting website.
					</li>
				</ol>
				<p>Authorisation of person being vetted:</p>
				<ol type="i">
					<li>I confirm that the information I have provided in this form relates to me and is correct.</li>
					<li>I have read and understood the information above. </li>
					<li>
						I authorise New Zealand Police to disclose any personal information relevant to my application (as described
						above) to the agency making this request for the purpose of assessing my suitability.
					</li>
				</ol>
			</div>
		</section>
	);
};

const VevoTermsAndConditionsView = () => {
	return (
		<section className="tcWrapper">
			<div className="tcs">
				<p>
					By clicking YES, I grant VETTING.com permission to request from the Department of Home Affairs a VEVO check in
					order to confirm the status of my Australian Visa.
				</p>
				<p>
					I understand that VETTING.com may be requesting this VEVO check on behalf of a third party who requires it in
					order to confirm my work, residency, or student visa status and that the results of this VEVO check will be
					returned by VETTING.com to that third party.
				</p>
				<p>
					I understand that the VEVO check will return details of my current Australian Visa status including (but not
					limited to) the expiry date of my visa; whether I am currently in Australia or offshore; and any work or study
					conditions attached to my visa.
				</p>
				<p>
					I understand that VETTING.com may use a third party provider to procure this VEVO check and that VETTING.com
					and any third party provider are required by the Department of Home Affairs to hold on record certain
					information obtained from this VEVO check which, if I am not entitled to be in Australia, the Commonwealth may
					request from VETTING.com, (and/or its third party provider), and subsequently use to locate me.
				</p>
			</div>
		</section>
	);
};

const AfpTermsAndConditionsView = () => {
	return (
		<section className="tcWrapper">
			<div className="achcTcs">
				<ol>
					<li>
						I acknowledge I have read the{' '}
						<a
							href="https://www.afp.gov.au/what-we-do/services/criminal-records/national-police-checks#process"
							target="_blank"
							rel="noreferrer"
						>
							Application Completion Guide
						</a>{' '}
						for this application form and I am aware exclusions from{' '}
						<a
							href="https://www.afp.gov.au/what-we-do/services/criminal-records/spent-convictions-scheme"
							target="_blank"
							rel="noreferrer"
						>
							spent convictions legislation
						</a>{' '}
						may apply to some categories of NPCs.
					</li>
					<li>
						The personal information I have provided on this form (including fingerprints if supplied) relates to me and
						is correct.
					</li>
					<li>
						I acknowledge the details contained on this form, including fingerprints where relevant, will be forwarded
						to the AFP, Australian Criminal Intelligence Commission, and/or the Police Services of the States or
						Territories of the Commonwealth of Australia.
					</li>
					<li>
						I consent to the AFP and any other Australian police force extracting details of any convictions, findings
						of guilt or pending court proceedings relating to me, including in relation to any traffic offence, and
						providing that information to me or to{' '}
						<a href="https://vetting.com/" target="_blank" rel="noreferrer">
							VETTING.com
						</a>{' '}
						and the legal entity customer.
					</li>
					<li>
						I acknowledge the information provided on this form will not be used without my prior consent for any other
						purpose, unless otherwise authorised by law.
					</li>
					<li>
						I acknowledge that any information provided on this form or disclosed by the police as a result of the
						records check may be taken into account by VETTING.com or any organisation to whom I present the results of
						the records check in assessing my suitability to receive the entitlement.
					</li>
				</ol>
			</div>
		</section>
	);
};

const CheckboxWrapper = styled.div`
	display: flex;
	padding-bottom: 15px;
`;

export const QualificationDeclaration = () => {
	return (
		<span className="consentContent">
			I confirm that the information I have given in connection with my application is correct to the best of my
			knowledge.
		</span>
	);
};

export const InternationalCheckDeclaration = (props: InternationalCheckProps) => {
	return (
		<span className="consentContent">
			By signing this Acknowledgement, I understand that the screening searches, to the extent applicable law allows and
			in accordance with applicable laws, will be conducted and my personal information, including sensitive personal
			information, will be utilized for this process. I agree to assist the Company, VETTING.com, and any Service
			Providers, in verifying and collecting this information, and to provide any additional information requested in
			connection with my application for employment with the Company. If I do not provide this information, the Company
			may not be able to make a thorough assessment of my suitability for employment and it may impact my application. I
			authorize any individual, corporation or other private or public entity to provide the Company and Provider, and
			their representatives, all relevant information about me to fulfil the purposes of this background screen.
			<p />
			<br />
			<p>
				I authorize any individual, corporation or other private or public entity to provide the Company and Provider,
				and their representatives, all relevant information about me to fulfil the purposes of this background screen.
			</p>
			<br />
			<p>
				<a href="#" onClick={() => props.setIsTermsAndConditionsVisible(true)}>
					View the full notice and authorization statement
				</a>
			</p>
		</span>
	);
};

interface InternationalCheckTermsAndConditionsViewProps {
	customerName: string;
}

interface InternationalCheckProps {
	setIsTermsAndConditionsVisible: (isVisible: boolean) => void;
}

const InternationCheckTermsView = (props: InternationalCheckTermsAndConditionsViewProps) => {
	return (
		<>
			<HeaderWrapper>
				<Header2>NOTICE AND AUTHORIZATION FOR BACKGROUND SCREENING INFORMATION</Header2>
				<Header3>APPLICANTS FOR EMPLOYMENT</Header3>
			</HeaderWrapper>
			<ContentWrapper>
				<p>
					To research and verify about you for <u>Pre employment checks for {props.customerName}</u>{' '}
					(&quot;Company&quot;) has engaged a background screening provider, VETTING.com (&quot;Provider&quot;). The
					Provider, and its agents, may verify and research the information provided, which may include your personal
					background, professional standing, work history, and qualifications. This notice is intended to describe the
					personal information collected and processed by the Company and Provider, and to obtain your consent to these
					activities.
				</p>
				<Header3> WHAT TYPES OF INFORMATION DOES THIS NOTICE COVER?</Header3>
				<p>
					The Company determines the specific screening services to be performed by the Provider. Company has instructed
					its Provider, and their agents, to collect and use the following types of information in order to conduct your
					employment screening:
					<li>Identification and address information.</li>
					<li>
						Financial information relating to debt, payment history, bankruptcy and collection matters, financial
						judgments, litigation.
					</li>
					<li>
						Criminal proceedings, convictions, and involvement in litigation, including civil suits where you were
						either a plaintiff or defendant.
					</li>
				</p>
				<Header3>HOW WILL THE PERSONAL INFORMATION SUBMITTED BE USED?</Header3>
				<p>
					Company will receive information from you and / or the Company and will use the details you have provided to
					confirm information or to obtain information from appropriate third-party sources. Company may share your
					information with sub-processors, agents, and data sources (&quot;Service Providers&quot;) to complete the
					requested report. The completed report will be shared with the Company, who will make any decisions regarding
					your suitability for the position or employment. Company, and any Service Providers, will use your information
					only in connection with this individual request or for purposes compatible with the original reason for the
					collection. (Examples of functions compatible with the request include compliance, security, and accuracy
					monitoring.) Company and Service Providers will not re-use or re-sell your information.
				</p>
				<Header3>CONSEQUENCES OF NOT PROVIDING INFORMATION</Header3>
				<p>
					If you do not provide the information required for the requested employment screening request or do not
					provide your authorization, the Company may not be able to assess your suitability for the position, which may
					affect your application or employment position.
				</p>
				<Header3>DATA TRANSFER AND RETENTION</Header3>
				<p>
					Company will store your data in one of its data storage facilities which are in the United Kingdom. Your
					personal information may be transferred to the United States (&quot;U.S.&quot;) by whatever means is
					determined to be appropriate. Your information may also be processed and stored in other countries where
					Service Providers are located or where your data will be located, such as with references. Your information
					may be subject to other country&apos;s laws for disclosure and may be accessible to foreign law enforcement,
					national security agencies, or courts. Please note that the laws regarding personal information in some of the
					jurisdictions involved may be different or less stringent than the laws of the country in which you reside.
					Company utilizes a variety of methods to transfer personal information across country borders. This can
					include consent, contractual methods, and/or regulatory authority&apos;s certifications.
				</p>
				<Header3>YOUR RIGHTS</Header3>
				<p>
					You have certain rights in respect to your personal data, including the right to access, correct, and in some
					cases to request the erasure of your personal data. Company and Provider will comply with requests to exercise
					your rights in accordance with applicable law. There are limitations to these rights, and there may be
					circumstances where Company or Provider are not able to comply with your request. To make any requests
					regarding your personal data, or if you have any questions or concerns regarding your personal data, contact
					the Company using the details below.
				</p>
				<br />
				<p>Company: {props.customerName}</p>
				<p> Provider: VETTING.com</p>
				<Header3>WITHDRAWAL OF CONSENT</Header3>
				<p>
					If you wish to withdraw your consent for these background checks, contact the Company. You may also contact
					Provider at: <a href="mailto:support@vetting.com">support@vetting.com.</a> The ability to stop any background
					check in process may not be instant; every attempt will be made to stop processing as quickly as possible.
				</p>
				<Header3>ACKNOWLEDGEMENT</Header3>
				<p>
					By signing this Acknowledgement, I understand that the screening searches, to the extent applicable law allows
					and in accordance with applicable laws, will be conducted and my personal information, including sensitive
					personal information, will be utilized for this process. I agree to assist Company, Provider, and any Service
					Providers, in verifying and collecting this information, and to provide any additional information requested
					in connection with my application for employment with the Company. If I do not provide this information, the
					Company may not be able to make a thorough assessment of my suitability for employment and it may impact my
					application.
				</p>
				<br />
				<p>
					I authorize any individual, corporation or other private or public entity to provide the Company and Provider,
					and their representatives, all relevant information about me to fulfil the purposes of this background screen.
				</p>
			</ContentWrapper>
		</>
	);
};

const Header2 = styled.h2`
	font-style: bold;
`;

const Header3 = styled.h3`
	font-style: bold;
	margin: 20px 0px 5px 0;
`;

const ContentWrapper = styled.div`
	padding: 20px;
`;

const HeaderWrapper = styled.div`
	text-align: center;
`;

const ConsentContainer = styled.ul`
	margin-top: 0px;
`;
